import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  CITIES_DROPDOWN,
  CITIES_DROPDOWN_INTERFACE,
  FETCH_REVIEWS,
  FETCH_REVIEWS_INTERFACE
} from '../../api/reviews';
import { REVIEWS_PAGE, REVIEWS_PAGE_INTERFACE } from '../../api/reviewsPage';
import ReviewsHeader from '../../components/Reviews/Header';
import ReviewsFilterNav from '../../components/Reviews/ReviewsFilternav';
import DisplayReviews from '../../components/Reviews/DisplayReviews';
import FooterAbout from '../../components/Reviews/FooterAbout';
import PageHero from '../../layouts/PageHero';

const Newsletter = dynamic(() => import('../../layouts/Newsletter/Newsletter'));
import Adventure from '../../components/Reviews/Adventure';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import ReviewRequest from '../../components/Review/ReviewRequest';
import client from '../../apollo-client';
import { replaceTripshepherdWithSeeSight } from '../../utils/trip-sheppered-text';

interface IProps {
  reviewsPage: REVIEWS_PAGE_INTERFACE['reviewsPage'];
  citiesPageDropDown: CITIES_DROPDOWN_INTERFACE;
}
const filterOptions: any = {
  Newest: {
    order_by: [{ created_at: 'desc' }]
  },
  Oldest: {
    order_by: [{ created_at: 'asc' }]
  },
  Highest: {
    order_by: [{ rating: 'desc' }]
  },
  Lowest: {
    order_by: [{ rating: 'asc' }]
  }
};
const Reviews = ({ reviewsPage, citiesPageDropDown }: IProps) => {
  const [sortOrder, setSortOrder] = useState<string>('Newest');
  const [currentPage, setCurrrentPage] = useState<number>(0);
  const [cityFilter, setCityFilter] = useState<any>(null);
  const [citiesToggle, setCitiesToggle] = useState<boolean>(false);
  const [sortToggle, setSortToggle] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const router = useRouter();
  const reviewId = router.query.id;

  //fetching queries
  const [
    fetchReviews,
    {
      data: totalReviews,
      error: fetchReviewsError,
      loading: fetchReviewsLoading,
      refetch: refetchReviews
    }
  ] = useLazyQuery<FETCH_REVIEWS_INTERFACE>(FETCH_REVIEWS);
  useEffect(() => {
    const whereCondition =
      cityFilter?.cities?.id !== undefined && cityFilter?.cities?.id !== 0
        ? {
            parentproduct: {
              boat: {
                cities_products: {
                  city: { strapi_city: { id: { _eq: cityFilter?.cities?.id } } }
                }
              }
            },
            date: { _is_null: false }
          }
        : { date: { _is_null: false } };

    fetchReviews({
      variables: {
        offset: currentPage * 10,
        // limit: 10,
        order_by: filterOptions[sortOrder].order_by[0],
        cityObject: cityFilter,
        where: whereCondition
      }
    });
    if (refetch) {
      refetchReviews();
      setRefetch(false);
    }
  }, [cityFilter, sortOrder, currentPage, cityFilter?.cities?.id, refetch]);

  useEffect(() => {
    const handleScroll = () => {
      setCitiesToggle(false);
      setSortToggle(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return (
    <>
      <Head>
        <meta
          name="og:title"
          content={replaceTripshepherdWithSeeSight(reviewsPage?.pageTitle)}
        />
        <meta
          name="og:description"
          key="metadescription"
          content={
            'Discover what travelers love about See Sight Tours! Read real reviews about our personalized, small-group guided tours and unforgettable experiences. '
          }
        />
        <title>{replaceTripshepherdWithSeeSight(reviewsPage?.pageTitle)}</title>
        <meta
          name="description"
          key="metadescription"
          content={
            'Discover what travelers love about See Sight Tours! Read real reviews about our personalized, small-group guided tours and unforgettable experiences. '
          }
        />
        <meta name="og:image" content="/vertical-seesight-logo.png" />
        {/* <meta name="viewport" content="width=device-width, initial-scale=1" /> */}
        <link
          href={replaceTripshepherdWithSeeSight(reviewsPage?.canonical)}
          rel="canonical"
          key="canonical"
        />
      </Head>
      <PageHero
        title="FROM ONE TRAVELER TO ANOTHER"
        snippet="See What Customers Are Saying About See Sight Tours"
        media={`https://res.cloudinary.com/see-sight-tours/video/upload/v1634145738/strapi/NiagaraFallsCanadaPage-Mobile-500x200.mp4`}
        video={true}
        totalReviews={totalReviews}
      />
      <div
        onClick={() => {
          if (citiesToggle) {
            setCitiesToggle(false);
          }
          if (sortToggle) {
            setSortToggle(false);
          }
        }}
        className="relative pb-16"
      >
        <ReviewsHeader
          totalReviews={totalReviews ? totalReviews : {}}
          citiesPageDropDown={citiesPageDropDown?.citiesPage?.cities || []}
          citiesDropDown={citiesPageDropDown?.citiesPage || {}}
          setRefetch={setRefetch}
        />
        <ReviewsFilterNav
          cityFilter={cityFilter}
          setCityFilter={setCityFilter}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          citiesDropdown={citiesPageDropDown?.citiesPage?.cities || []}
          citiesToggle={citiesToggle}
          setCitiesToggle={setCitiesToggle}
          sortToggle={sortToggle}
          setSortToggle={setSortToggle}
          currentPage={currentPage}
          setCurrentPage={setCurrrentPage}
        />
        <DisplayReviews
          currentPage={currentPage}
          setCurrentPage={setCurrrentPage}
          totalReviews={totalReviews ? totalReviews : {}}
          fetchReviewsLoading={fetchReviewsLoading}
        />
        <FooterAbout reviewPage={reviewsPage} />
        <Adventure />
        <Newsletter />
        {reviewId && reviewId.length > 4 && <ReviewRequest id={reviewId} />}
      </div>
    </>
  );
};

export async function getServerSideProps() {
  const {
    data: { reviewsPage } = {},
    loading,
    error
  } = await client.query<REVIEWS_PAGE_INTERFACE>({
    query: REVIEWS_PAGE
  });

  const { data: citiesPageDropDown, error: citiesDropdownError } =
    await client.query<CITIES_DROPDOWN_INTERFACE>({
      query: CITIES_DROPDOWN
    });

  return {
    props: {
      reviewsPage:
        reviewsPage &&
        JSON.parse(
          replaceTripshepherdWithSeeSight(JSON.stringify(reviewsPage))
        ),
      citiesPageDropDown:
        citiesPageDropDown &&
        JSON.parse(
          replaceTripshepherdWithSeeSight(JSON.stringify(citiesPageDropDown))
        )
    }
  };
}

export default Reviews;
